import React from 'react';
import {
  FileInput, FileText, CheckCircle2, Receipt, CreditCard, Factory, User, Package,
  Mail, ClipboardList, Banknote, CircleDot, ArrowRight, Image as ImageIcon,
  type LucideIcon,
} from 'lucide-react';
import type { ChatResultItem, ChatCard, ChatBreakdown, ChatOperation, ChatAction } from '../../lib/chatApi';
import { MarkdownContent } from './MarkdownContent';
import { DataTable, type Column } from './DataTable';
import { MetricCard } from './MetricCard';
import { TYPE, MEASURE, FOCUS } from '../ui/tokens';

/**
 * A turn in the transcript.
 *
 * The rule the layout follows, in reading order: what is the answer, what is the key number, what
 * supporting detail exists, what can I do next. If the eye does not travel in that order the layout
 * is wrong however good it looks — so the reply comes first, figures next, records after that, and
 * anything ancillary is folded away.
 */

/** Icons, not emoji. An emoji has no accessible name and reads as gibberish to a screen reader. */
const TYPE_ICON: Record<string, LucideIcon> = {
  rfq: FileInput,
  quotation: FileText,
  // A line of one quotation (quotation_detail, quotation_margin) opens that quotation.
  quotation_line: FileText,
  confirmed_order: CheckCircle2,
  vendor_invoice: Receipt,
  client_invoice: CreditCard,
  vendor: Factory,
  client: User,
  inventory: Package,
  // The catalogue answers arrive typed as 'product'; without an entry they fell back to a
  // generic dot, which is the 🔹 in the screenshots.
  product: Package,
  email: Mail,
  vendor_po: ClipboardList,
  payable: Banknote,
};

/** A readable name for the record type, for the icon's accessible label. */
function typeLabel(type: string): string {
  return String(type || 'record').replace(/_/g, ' ');
}

/**
 * A file name that wraps where a reader would break it: after a "-", "_" or "." in the name, never
 * inside the extension. Without this a uuid name broke as "…629e9d.pd" / "f". A name with no such
 * points still wraps (break-words) rather than overflowing.
 */
function fileNameWithBreaks(name: string): React.ReactNode {
  const dot = name.lastIndexOf('.');
  const base = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '';
  const parts = base.split(/(?<=[-_.])/);
  return (
    <>
      {parts.map((part, i) => (
        <React.Fragment key={i}>{part}{i < parts.length - 1 && <wbr />}</React.Fragment>
      ))}
      {ext && <span className="whitespace-nowrap">{ext}</span>}
    </>
  );
}

/**
 * The document a turn was about, as a card: icon, then the file name on up to two lines. A 420px
 * panel cannot fit a uuid name like "93680814-9d48-4805-bf61-36a654629e9d.pdf" on one line, so it
 * wraps (anywhere: such names have no spaces) and shows in full; only a name longer than two lines
 * is cut with an ellipsis, and the whole name is in the tooltip and the accessible label.
 */
export const AttachmentCard: React.FC<{ name: string; tone?: 'onPrimary' | 'plain'; kind?: 'image' | 'file' }> = ({
  name, tone = 'plain', kind = 'file',
}) => {
  // A pasted image reads as an image, any other attachment as a document (D121.10).
  const Icon = kind === 'image' ? ImageIcon : FileText;
  return (
  <div
    className={[
      'flex min-w-0 max-w-full items-start gap-2 rounded-lg border px-2.5 py-1.5',
      tone === 'onPrimary' ? 'border-white/30 bg-white/15 text-white' : 'border-slate-200 bg-slate-50 text-slate-700',
    ].join(' ')}
    title={name}
    aria-label={`Attached file: ${name}`}
    role="note"
  >
    <Icon aria-hidden="true" size={16} className={tone === 'onPrimary' ? 'mt-px shrink-0 text-white' : 'mt-px shrink-0 text-red-600'} />
    <span className={`min-w-0 line-clamp-2 break-words ${TYPE.meta} font-medium`}>{fileNameWithBreaks(name)}</span>
  </div>
  );
};

export const UserMessage: React.FC<{ text: string; ts: number; attachmentName?: string; attachmentKind?: 'image' | 'file' }> = ({
  text, ts, attachmentName, attachmentKind,
}) => (
  <div className="flex flex-col items-end">
    {/* The user's own words are context, not content — compact, and never competing with the answer. */}
    <div className={`max-w-[85%] min-w-0 rounded-2xl rounded-tr-sm bg-primary-500 px-3 py-2 text-white ${TYPE.cell}`}>
      {attachmentName && <AttachmentCard name={attachmentName} tone="onPrimary" kind={attachmentKind} />}
      {text && <span className={`block whitespace-pre-wrap break-words${attachmentName ? ' mt-1.5' : ''}`}>{text}</span>}
    </div>
    <time className={`${TYPE.meta} text-slate-400 mt-0.5 pr-1`} dateTime={new Date(ts).toISOString()}>
      {new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
    </time>
  </div>
);

export type AssistantMessageProps = {
  text: string;
  ts: number;
  results?: ChatResultItem[];
  cards?: ChatCard[];
  breakdown?: ChatBreakdown[];
  responseType?: ChatOperation | null;
  /** The document this answer is about, when the question came with one. */
  attachmentName?: string;
  attachmentKind?: 'image' | 'file';
  /** "Open <screen>" links: shown for every answer shape, since a link is not a record. */
  actions?: ChatAction[];
  onResultClick: (link?: string) => void;
};

/** Records become a table once there are enough of them to be worth comparing. */
const TABLE_THRESHOLD = 4;

/** The columns a result list implies. Only ever these — a name question does not get a cost column. */
const RESULT_COLUMNS: Column[] = [
  { key: 'title', label: 'Name', maxWidth: '22rem' },
  { key: 'subtitle', label: 'Detail', maxWidth: '22rem' },
  { key: 'badge', label: 'Status', type: 'status' },
];

export const AssistantMessage: React.FC<AssistantMessageProps> = ({
  text, ts, results = [], cards = [], breakdown = [], responseType, attachmentName, attachmentKind, actions = [], onResultClick,
}) => {
  // A COUNT is answered by the number. Records under it are the defect this whole redesign began
  // with; the server already withholds them, and this is the second lock.
  const showRecords = responseType !== 'count' && results.length > 0;
  const asTable = showRecords && results.length >= TABLE_THRESHOLD;
  const headline = cards.length === 1;

  return (
    <div className="flex flex-col items-start w-full">
      <div className={`w-full rounded-2xl rounded-tl-sm border border-slate-200 bg-white px-3.5 py-3 ${TYPE.body} text-slate-800`}>
        {/* 0 — which document this answers, so the pair stays identifiable when scrolling back */}
        {attachmentName && (
          <div className="mb-2 flex min-w-0 items-center gap-1.5">
            <span className={`shrink-0 ${TYPE.meta} text-slate-500`}>About</span>
            <AttachmentCard name={attachmentName} kind={attachmentKind} />
          </div>
        )}
        {/* 1 — the answer */}
        <MarkdownContent>{text}</MarkdownContent>

        {/* 1b — where to go: "Open Quote Generator". Paths come from the server's screen table. */}
        {actions.length > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-2">
            {actions.map((action) => (
              <button
                key={action.path}
                type="button"
                onClick={() => onResultClick(action.path)}
                className={`inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-3 py-1.5 text-white hover:bg-primary-600 ${TYPE.cell} font-semibold ${FOCUS}`}
              >
                {action.label}
                <ArrowRight aria-hidden="true" size={14} />
              </button>
            ))}
          </div>
        )}

        {/* 2 — the key figure */}
        {cards.length > 0 && (
          <div className={headline ? 'mt-2.5' : 'mt-2.5 grid gap-2 sm:grid-cols-2'}>
            {cards.map((card, i) => (
              <MetricCard
                key={`${card.label}-${i}`}
                label={card.label}
                value={card.value}
                sub={card.sub}
                emphasis={headline}
              />
            ))}
          </div>
        )}

        {breakdown.length > 0 && (
          <dl className="mt-2.5 divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
            {breakdown.map((row, i) => (
              <div key={`${row.label}-${i}`} className="flex items-center justify-between gap-3 px-3 py-1.5 bg-white">
                <dt className={`${TYPE.cell} font-medium capitalize text-slate-700`}>{row.label.replace(/_/g, ' ')}</dt>
                <dd className={`${TYPE.cell} ${TYPE.figure} text-slate-800`}>
                  {[row.count != null ? row.count : null, row.outstanding, row.total]
                    .filter((v) => v !== null && v !== undefined && v !== '')
                    .join(' · ')}
                </dd>
              </div>
            ))}
          </dl>
        )}

        {/* 3 — the records */}
        {showRecords && (asTable ? (
          <div className="mt-2.5">
            <DataTable
              columns={RESULT_COLUMNS}
              rows={results.map((r) => ({ ...r }))}
              caption="Matching records"
              onRowClick={(row) => onResultClick(row.link as string | undefined)}
              isRowClickable={(row) => Boolean(row.link)}
            />
          </div>
        ) : (
          <ul className="mt-2.5 space-y-1.5">
            {results.map((item) => {
              const Icon = TYPE_ICON[item.type] || CircleDot;
              const openable = Boolean(item.link);
              const row = (
                <span className="flex items-start gap-2">
                  <Icon aria-hidden="true" size={16} className={`mt-0.5 shrink-0 text-slate-400 ${openable ? 'group-hover:text-primary-600' : ''}`} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className={`${TYPE.cell} font-semibold text-slate-900 truncate`}>{item.title}</span>
                      {item.badge ? (
                        <span className="text-xs uppercase tracking-wide text-slate-500 shrink-0">{item.badge}</span>
                      ) : null}
                    </span>
                    {item.subtitle ? (
                      <span className={`${TYPE.meta} block text-slate-600 truncate`} title={item.subtitle}>
                        {item.subtitle}
                      </span>
                    ) : null}
                    {openable ? <span className="sr-only">{` - open this ${typeLabel(item.type)}`}</span> : null}
                  </span>
                  {openable ? (
                    <ArrowRight aria-hidden="true" size={14} className="mt-1 shrink-0 text-slate-300 group-hover:text-primary-500" />
                  ) : null}
                </span>
              );
              return (
                <li key={`${item.type}-${item.id}`}>
                  {openable ? (
                    // A real button with an accessible name, not a clickable div.
                    <button
                      type="button"
                      onClick={() => onResultClick(item.link)}
                      className={`group w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-left hover:border-primary-500 hover:bg-primary-50 ${FOCUS}`}
                    >
                      {row}
                    </button>
                  ) : (
                    // No link means nothing to open: plain text, never a button that does nothing.
                    <div className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-left">
                      {row}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        ))}
      </div>
      <time className={`${TYPE.meta} text-slate-400 mt-0.5 pl-1`} dateTime={new Date(ts).toISOString()}>
        {new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </time>
    </div>
  );
};

export { MEASURE };
